<template>
    <div class="p-3">
        <div
            class="d-flex align-items-center justify-content-between gap-2 mb-2"
        >
            <div>
                <h3 class="mb-0">Pengisian ICD Rawat Inap</h3>
                <small class="text-muted">
                    Riwayat pasien berdasarkan satu nomor registrasi
                </small>
            </div>

            <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="$router.back()"
            >
                <i class="fa fa-arrow-left me-1"></i>
                Kembali
            </button>
        </div>

        <div v-if="loadingPage" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
            <div class="mt-2 text-muted">Memuat data pasien...</div>
        </div>

        <div v-else-if="errorPage" class="alert alert-danger">
            <div class="fw-semibold">Data gagal dimuat</div>
            <div>{{ errorPage }}</div>

            <button
                type="button"
                class="btn btn-sm btn-outline-danger mt-2"
                @click="loadPage"
            >
                <i class="fa fa-refresh me-1"></i>
                Muat Ulang
            </button>
        </div>

        <template v-else>
            <div class="card border-0 shadow-sm mb-3">
                <div class="card-header bg-primary text-white fw-semibold">
                    <i class="fa fa-user me-1"></i>
                    Data Pasien
                </div>

                <div class="card-body patient-card-body">
                    <div class="patient-grid">
                        <div
                            v-for="item in patientInfo"
                            :key="item.label"
                            class="patient-item"
                            :class="item.className || ''"
                        >
                            <div class="patient-label">
                                {{ item.label }}
                            </div>

                            <div class="patient-value">
                                {{ showValue(item.value) }}
                            </div>
                        </div>
                    </div>

                    <div class="patient-status">
                        <span
                            class="badge"
                            :class="
                                pasien.StatusPerawatan === 'Masih Dirawat'
                                    ? 'bg-warning text-dark'
                                    : 'bg-success'
                            "
                        >
                            {{ showValue(pasien.StatusPerawatan) }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="row g-3 align-items-stretch workspace-row">
                <div class="col-12 col-xl-4">
                    <div class="card border-0 shadow-sm icd-card">
                        <div
                            class="card-header bg-primary text-white fw-semibold"
                        >
                            <i class="fa fa-pencil-square-o me-1"></i>
                            Pengisian ICD-10 dan ICD-9
                        </div>

                        <div class="card-body">
                            <div v-if="loading.icd" class="loading-section">
                                <i class="fa fa-spinner fa-spin me-1"></i>
                                Memuat ICD...
                            </div>

                            <template v-else>
                                <div class="alert alert-info py-2 small">
                                    Baris pertama merupakan diagnosis utama.
                                    Baris berikutnya merupakan diagnosis atau
                                    prosedur sekunder.
                                </div>

                                <div
                                    v-for="(row, index) in icdRows"
                                    :key="row.key"
                                    class="icd-row"
                                >
                                    <div
                                        class="d-flex align-items-center justify-content-between gap-2 mb-2"
                                    >
                                        <span
                                            class="badge"
                                            :class="
                                                index === 0
                                                    ? 'bg-primary'
                                                    : 'bg-secondary'
                                            "
                                        >
                                            {{
                                                index === 0
                                                    ? "Diagnosis Utama"
                                                    : `Diagnosis Sekunder ${index}`
                                            }}
                                        </span>

                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            :disabled="saving"
                                            @click="removeIcd(index)"
                                        >
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>

                                    <div class="mb-3">
                                        <label
                                            class="form-label small fw-semibold"
                                        >
                                            ICD-10
                                        </label>

                                        <v-select
                                            v-model="row.diagnosis"
                                            :options="icd10Options"
                                            :filterable="false"
                                            :clearable="true"
                                            label="name"
                                            placeholder="Ketik kode atau diagnosis ICD-10..."
                                            @search="searchIcd10"
                                            @input="setIcd10($event, index)"
                                        >
                                            <template #no-options>
                                                Ketik minimal satu karakter
                                            </template>
                                        </v-select>
                                    </div>

                                    <div>
                                        <label
                                            class="form-label small fw-semibold"
                                        >
                                            ICD-9
                                        </label>

                                        <v-select
                                            v-model="row.procedure"
                                            :options="icd9Options"
                                            :filterable="false"
                                            :clearable="true"
                                            label="name"
                                            placeholder="Ketik kode atau prosedur ICD-9..."
                                            @search="searchIcd9"
                                            @input="setIcd9($event, index)"
                                        >
                                            <template #no-options>
                                                Ketik minimal satu karakter
                                            </template>
                                        </v-select>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    class="btn btn-outline-primary w-100 mt-3"
                                    :disabled="saving"
                                    @click="addIcd"
                                >
                                    <i class="fa fa-plus-circle me-1"></i>
                                    Tambah Diagnosis
                                </button>

                                <button
                                    type="button"
                                    class="btn btn-success w-100 mt-2"
                                    :disabled="saving"
                                    @click="saveIcd"
                                >
                                    <span
                                        v-if="saving"
                                        class="spinner-border spinner-border-sm me-1"
                                    ></span>

                                    <i v-else class="fa fa-save me-1"></i>

                                    {{ saving ? "Menyimpan..." : "Simpan ICD" }}
                                </button>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="col-12 col-xl-8">
                    <div class="card border-0 shadow-sm history-card">
                        <div class="card-header bg-white fw-semibold">
                            <i class="fa fa-folder-open text-success me-1"></i>
                            Riwayat Periode Rawat Inap
                        </div>

                        <div class="card-body p-0 history-scroll">
                            <div
                                class="accordion history-accordion"
                                id="historyAccordion"
                            >
                                <div class="accordion-item">
                                    <h2 class="accordion-header">
                                        <button
                                            class="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#resumeSection"
                                            @click="loadHistory('resume')"
                                        >
                                            <i
                                                class="fa fa-file-pdf-o text-danger me-2"
                                            ></i>

                                            Resume Medis Final

                                            <span
                                                v-if="resumeAvailable"
                                                class="badge bg-secondary ms-2"
                                            >
                                                1
                                            </span>
                                        </button>
                                    </h2>

                                    <div
                                        id="resumeSection"
                                        class="accordion-collapse collapse"
                                        data-bs-parent="#historyAccordion"
                                    >
                                        <div class="accordion-body">
                                            <div
                                                v-if="loading.resume"
                                                class="loading-section"
                                            >
                                                <i
                                                    class="fa fa-spinner fa-spin me-1"
                                                ></i>
                                                Memeriksa resume medis...
                                            </div>

                                            <div v-else-if="resumeAvailable">
                                                <button
                                                    type="button"
                                                    class="btn btn-outline-danger"
                                                    @click="openResumePdf"
                                                >
                                                    <i
                                                        class="fa fa-file-pdf-o me-1"
                                                    ></i>
                                                    Lihat Resume Medis PDF
                                                </button>
                                            </div>

                                            <EmptyState
                                                v-else
                                                text="Resume medis final belum tersedia."
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="accordion-item">
                                    <h2 class="accordion-header">
                                        <button
                                            class="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#cpptSection"
                                            @click="loadHistory('cppt')"
                                        >
                                            <i
                                                class="fa fa-stethoscope text-success me-2"
                                            ></i>

                                            CPPT Periode Rawat

                                            <span
                                                v-if="loaded.cppt"
                                                class="badge bg-secondary ms-2"
                                            >
                                                {{ cppt.length }}
                                            </span>
                                        </button>
                                    </h2>

                                    <div
                                        id="cpptSection"
                                        class="accordion-collapse collapse"
                                        data-bs-parent="#historyAccordion"
                                    >
                                        <div class="accordion-body">
                                            <div
                                                v-if="loading.cppt"
                                                class="loading-section"
                                            >
                                                <i
                                                    class="fa fa-spinner fa-spin me-1"
                                                ></i>
                                                Memuat CPPT...
                                            </div>

                                            <div
                                                v-else-if="cppt.length"
                                                class="table-responsive"
                                            >
                                                <table
                                                    class="table table-bordered table-sm cppt-table mb-0"
                                                >
                                                    <thead class="table-light">
                                                        <tr>
                                                            <th
                                                                style="
                                                                    width: 140px;
                                                                "
                                                            >
                                                                Tanggal
                                                            </th>
                                                            <th>SOAP</th>
                                                            <th
                                                                style="
                                                                    width: 220px;
                                                                "
                                                            >
                                                                Instruksi
                                                            </th>
                                                            <th
                                                                style="
                                                                    width: 150px;
                                                                "
                                                            >
                                                                Petugas
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        <tr
                                                            v-for="row in cppt"
                                                            :key="row.IdCPPT"
                                                            :class="{
                                                                'cppt-row-perawat':
                                                                    isCpptPerawat(
                                                                        row
                                                                    ),
                                                                'cppt-row-dokter':
                                                                    !isCpptPerawat(
                                                                        row
                                                                    ),
                                                            }"
                                                        >
                                                            <td>
                                                                {{
                                                                    formatDateTime(
                                                                        row.CreatedAt ||
                                                                            row.UpdatedAt
                                                                    )
                                                                }}

                                                                <div
                                                                    v-if="
                                                                        row.ApproveCPPT
                                                                    "
                                                                    class="text-success small mt-2"
                                                                >
                                                                    <i
                                                                        class="fa fa-check-circle"
                                                                    ></i>
                                                                    Read back
                                                                    CPPT
                                                                </div>
                                                            </td>

                                                            <td
                                                                class="cppt-soap-cell"
                                                            >
                                                                <div
                                                                    class="cppt-soap-content"
                                                                    :class="{
                                                                        'cppt-soap-perawat':
                                                                            isCpptPerawat(
                                                                                row
                                                                            ),
                                                                        'cppt-soap-dokter':
                                                                            !isCpptPerawat(
                                                                                row
                                                                            ),
                                                                    }"
                                                                >
                                                                    <div
                                                                        class="cppt-soap-line"
                                                                    >
                                                                        <strong
                                                                            >S:</strong
                                                                        >
                                                                        <span>{{
                                                                            showValue(
                                                                                row.Subjective
                                                                            )
                                                                        }}</span>
                                                                    </div>

                                                                    <div
                                                                        class="cppt-soap-line"
                                                                    >
                                                                        <strong
                                                                            >O:</strong
                                                                        >
                                                                        <span>{{
                                                                            showValue(
                                                                                row.Objective
                                                                            )
                                                                        }}</span>
                                                                    </div>

                                                                    <div
                                                                        class="cppt-soap-line"
                                                                    >
                                                                        <strong
                                                                            >A:</strong
                                                                        >
                                                                        <span>{{
                                                                            showValue(
                                                                                row.Assessment
                                                                            )
                                                                        }}</span>
                                                                    </div>

                                                                    <div
                                                                        class="cppt-soap-line"
                                                                    >
                                                                        <strong
                                                                            >P:</strong
                                                                        >
                                                                        <span>{{
                                                                            showValue(
                                                                                row.Planning
                                                                            )
                                                                        }}</span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td
                                                                class="pre-wrap"
                                                            >
                                                                {{
                                                                    showValue(
                                                                        instruction(
                                                                            row
                                                                        )
                                                                    )
                                                                }}
                                                            </td>

                                                            <td>
                                                                <div
                                                                    class="fw-semibold"
                                                                >
                                                                    {{
                                                                        showValue(
                                                                            row.SavedBy
                                                                        )
                                                                    }}
                                                                </div>

                                                                <div
                                                                    class="small text-muted"
                                                                >
                                                                    {{
                                                                        showValue(
                                                                            row.Dokter
                                                                        )
                                                                    }}
                                                                </div>

                                                                <span
                                                                    v-if="
                                                                        row.DiisiOleh
                                                                    "
                                                                    class="badge bg-light text-dark border mt-1"
                                                                >
                                                                    {{
                                                                        row.DiisiOleh
                                                                    }}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>

                                            <EmptyState
                                                v-else
                                                text="Tidak ada CPPT pada registrasi ini."
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="accordion-item">
                                    <h2 class="accordion-header">
                                        <button
                                            class="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#labSection"
                                            @click="loadHistory('lab')"
                                        >
                                            <i
                                                class="fa fa-flask text-primary me-2"
                                            ></i>

                                            Hasil Laboratorium

                                            <span
                                                v-if="loaded.lab"
                                                class="badge bg-secondary ms-2"
                                            >
                                                {{ lab.length }}
                                            </span>
                                        </button>
                                    </h2>

                                    <div
                                        id="labSection"
                                        class="accordion-collapse collapse"
                                        data-bs-parent="#historyAccordion"
                                    >
                                        <div class="accordion-body">
                                            <div
                                                v-if="loading.lab"
                                                class="loading-section"
                                            >
                                                <i
                                                    class="fa fa-spinner fa-spin me-1"
                                                ></i>
                                                Memuat laboratorium...
                                            </div>

                                            <template v-else-if="lab.length">
                                                <button
                                                    v-for="(row, index) in lab"
                                                    :key="row.Notran || index"
                                                    type="button"
                                                    class="history-link"
                                                    @click="openLab(row)"
                                                >
                                                    <span>
                                                        <i
                                                            class="fa fa-flask me-1"
                                                        ></i>

                                                        {{
                                                            row.date ||
                                                            formatDateTime(
                                                                row.Tglhasil ||
                                                                    row.Tanggal
                                                            )
                                                        }}
                                                    </span>

                                                    <i
                                                        class="fa fa-chevron-right"
                                                    ></i>
                                                </button>
                                            </template>

                                            <EmptyState
                                                v-else
                                                text="Tidak ada hasil laboratorium pada registrasi ini."
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="accordion-item">
                                    <h2 class="accordion-header">
                                        <button
                                            class="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#radiologiSection"
                                            @click="loadHistory('radiologi')"
                                        >
                                            <i
                                                class="fa fa-photo text-primary me-2"
                                            ></i>

                                            Hasil Radiologi

                                            <span
                                                v-if="loaded.radiologi"
                                                class="badge bg-secondary ms-2"
                                            >
                                                {{ radiologi.length }}
                                            </span>
                                        </button>
                                    </h2>

                                    <div
                                        id="radiologiSection"
                                        class="accordion-collapse collapse"
                                        data-bs-parent="#historyAccordion"
                                    >
                                        <div class="accordion-body">
                                            <div
                                                v-if="loading.radiologi"
                                                class="loading-section"
                                            >
                                                <i
                                                    class="fa fa-spinner fa-spin me-1"
                                                ></i>
                                                Memuat radiologi...
                                            </div>

                                            <template
                                                v-else-if="radiologi.length"
                                            >
                                                <button
                                                    v-for="(
                                                        row, index
                                                    ) in radiologi"
                                                    :key="`${
                                                        row.NoTran || row.Notran
                                                    }-${index}`"
                                                    type="button"
                                                    class="history-link"
                                                    @click="openRadiologi(row)"
                                                >
                                                    <span>
                                                        <i
                                                            class="fa fa-photo me-1"
                                                        ></i>

                                                        {{
                                                            row.date ||
                                                            formatDateTime(
                                                                row.TglHasil ||
                                                                    row.Tanggal
                                                            )
                                                        }}

                                                        <span
                                                            v-if="row.NmTarif"
                                                            class="text-muted"
                                                        >
                                                            -
                                                            {{ row.NmTarif }}
                                                        </span>
                                                    </span>

                                                    <i
                                                        class="fa fa-chevron-right"
                                                    ></i>
                                                </button>
                                            </template>

                                            <EmptyState
                                                v-else
                                                text="Tidak ada hasil radiologi pada registrasi ini."
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="accordion-item">
                                    <h2 class="accordion-header">
                                        <button
                                            class="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#medicineSection"
                                            @click="loadHistory('obat')"
                                        >
                                            <i
                                                class="fa fa-medkit text-primary me-2"
                                            ></i>

                                            Obat Selama Perawatan

                                            <span
                                                v-if="loaded.obat"
                                                class="badge bg-secondary ms-2"
                                            >
                                                {{ obat.length }}
                                            </span>
                                        </button>
                                    </h2>

                                    <div
                                        id="medicineSection"
                                        class="accordion-collapse collapse"
                                        data-bs-parent="#historyAccordion"
                                    >
                                        <div class="accordion-body">
                                            <div
                                                v-if="loading.obat"
                                                class="loading-section"
                                            >
                                                <i
                                                    class="fa fa-spinner fa-spin me-1"
                                                ></i>
                                                Memuat obat...
                                            </div>

                                            <div
                                                v-else-if="obat.length"
                                                class="table-responsive"
                                            >
                                                <table
                                                    class="table table-bordered table-striped table-sm align-middle mb-0"
                                                >
                                                    <thead class="table-light">
                                                        <tr>
                                                            <th
                                                                style="
                                                                    width: 130px;
                                                                "
                                                            >
                                                                Tanggal
                                                            </th>
                                                            <th>Nama Obat</th>
                                                            <th
                                                                style="
                                                                    width: 80px;
                                                                "
                                                            >
                                                                Jumlah
                                                            </th>
                                                            <th
                                                                style="
                                                                    width: 140px;
                                                                "
                                                            >
                                                                Signa
                                                            </th>
                                                            <th>
                                                                Aturan Pakai
                                                            </th>
                                                            <th
                                                                style="
                                                                    width: 150px;
                                                                "
                                                            >
                                                                Dokter
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        <tr
                                                            v-for="(
                                                                row, index
                                                            ) in obat"
                                                            :key="`${row.BLCode}-${row.KodeObat}-${index}`"
                                                        >
                                                            <td>
                                                                {{
                                                                    formatMedicineDate(
                                                                        row
                                                                    )
                                                                }}
                                                            </td>

                                                            <td>
                                                                {{
                                                                    showValue(
                                                                        row.NamaObat
                                                                    )
                                                                }}
                                                            </td>

                                                            <td
                                                                class="text-center"
                                                            >
                                                                {{
                                                                    showValue(
                                                                        row.TotalQty ||
                                                                            row.Qty
                                                                    )
                                                                }}
                                                            </td>

                                                            <td>
                                                                {{
                                                                    showValue(
                                                                        row.Signa
                                                                    )
                                                                }}
                                                            </td>

                                                            <td>
                                                                {{
                                                                    showValue(
                                                                        row.NoteCaraMinumObat ||
                                                                            row.NoteSigna
                                                                    )
                                                                }}
                                                            </td>

                                                            <td>
                                                                {{
                                                                    showValue(
                                                                        row.NmDoc
                                                                    )
                                                                }}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>

                                            <EmptyState
                                                v-else
                                                text="Tidak ada obat selama perawatan pada registrasi ini."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div
            v-if="pdfViewer"
            class="viewer-overlay"
            @click.self="closeResumePdf"
        >
            <div class="viewer-window">
                <div class="viewer-toolbar">
                    <strong>Resume Medis Final</strong>

                    <div class="d-flex gap-2">
                        <a
                            :href="resumePdfUrl"
                            target="_blank"
                            class="btn btn-sm btn-outline-light"
                        >
                            <i class="fa fa-external-link me-1"></i>
                            Buka Tab Baru
                        </a>

                        <button
                            type="button"
                            class="btn btn-sm btn-outline-light"
                            @click="closeResumePdf"
                        >
                            <i class="fa fa-times me-1"></i>
                            Tutup
                        </button>
                    </div>
                </div>

                <iframe
                    :src="resumePdfUrl"
                    class="pdf-frame"
                    title="Resume Medis Final"
                ></iframe>
            </div>
        </div>

        <div
            v-if="detailViewer"
            class="viewer-overlay"
            @click.self="closeDetailViewer"
        >
            <div class="viewer-window">
                <div class="viewer-toolbar">
                    <strong>{{ detailViewer.title }}</strong>

                    <button
                        type="button"
                        class="btn btn-sm btn-outline-light"
                        @click="closeDetailViewer"
                    >
                        <i class="fa fa-times me-1"></i>
                        Tutup
                    </button>
                </div>

                <div class="viewer-content">
                    <div v-if="detailViewer.loading" class="text-center py-5">
                        <div class="spinner-border text-success"></div>
                        <div class="mt-2 text-muted">Memuat detail...</div>
                    </div>

                    <template v-else-if="detailViewer.type === 'lab'">
                        <div class="row g-3 mb-3">
                            <div class="col-md-4">
                                <div class="text-muted small">
                                    No. Laboratorium
                                </div>

                                <div class="fw-semibold">
                                    {{
                                        showValue(
                                            detailViewer.head.Nolab ||
                                                detailViewer.head.Notran
                                        )
                                    }}
                                </div>
                            </div>

                            <div class="col-md-4">
                                <div class="text-muted small">
                                    Tanggal Hasil
                                </div>

                                <div class="fw-semibold">
                                    {{
                                        formatDateTime(
                                            detailViewer.head.Tglhasil
                                        )
                                    }}
                                </div>
                            </div>

                            <div class="col-md-4">
                                <div class="text-muted small">
                                    Dokter Pengirim
                                </div>

                                <div class="fw-semibold">
                                    {{ showValue(detailViewer.head.NmDoc) }}
                                </div>
                            </div>
                        </div>

                        <div
                            v-for="(group, groupName) in detailViewer.groups"
                            :key="groupName"
                            class="mb-4"
                        >
                            <div
                                class="fw-bold text-success border-bottom pb-2 mb-2"
                            >
                                {{ groupName }}
                            </div>

                            <div class="table-responsive">
                                <table
                                    class="table table-bordered table-sm align-middle"
                                >
                                    <thead class="table-light">
                                        <tr>
                                            <th>Pemeriksaan</th>
                                            <th>Hasil</th>
                                            <th>Satuan</th>
                                            <th>Nilai Normal</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <template v-for="(item, code) in group">
                                            <tr :key="`head-${code}`">
                                                <td class="fw-semibold">
                                                    {{
                                                        showValue(
                                                            item.head.NMDetail
                                                        )
                                                    }}
                                                </td>

                                                <td
                                                    :class="{
                                                        'text-danger fw-bold':
                                                            isCriticalLab(
                                                                item.head
                                                            ),
                                                    }"
                                                >
                                                    {{
                                                        showValue(
                                                            item.head.Hasil
                                                        )
                                                    }}
                                                </td>

                                                <td>
                                                    {{
                                                        showValue(
                                                            item.head.Satuan
                                                        )
                                                    }}
                                                </td>

                                                <td>
                                                    {{ normalValue(item.head) }}
                                                </td>
                                            </tr>

                                            <tr
                                                v-for="detail in item.detail ||
                                                []"
                                                :key="`detail-${detail.KDDetail}`"
                                            >
                                                <td class="ps-4">
                                                    -
                                                    {{
                                                        showValue(
                                                            detail.NMDetail
                                                        )
                                                    }}
                                                </td>

                                                <td
                                                    :class="{
                                                        'text-danger fw-bold':
                                                            isCriticalLab(
                                                                detail
                                                            ),
                                                    }"
                                                >
                                                    {{
                                                        showValue(detail.Hasil)
                                                    }}
                                                </td>

                                                <td>
                                                    {{
                                                        showValue(detail.Satuan)
                                                    }}
                                                </td>

                                                <td>
                                                    {{ normalValue(detail) }}
                                                </td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <EmptyState
                            v-if="
                                !Object.keys(detailViewer.groups || {}).length
                            "
                            text="Detail laboratorium tidak tersedia."
                        />
                    </template>

                    <template v-else-if="detailViewer.type === 'radiologi'">
                        <div
                            v-for="(row, index) in detailViewer.rows"
                            :key="index"
                            class="mb-4 pb-4 border-bottom"
                        >
                            <div class="row g-3 mb-3">
                                <div class="col-md-4">
                                    <div class="text-muted small">
                                        Pemeriksaan
                                    </div>

                                    <div class="fw-semibold">
                                        {{ showValue(row.NmTarif) }}
                                    </div>
                                </div>

                                <div class="col-md-4">
                                    <div class="text-muted small">
                                        Tanggal Hasil
                                    </div>

                                    <div class="fw-semibold">
                                        {{ formatDateTime(row.TglHasil) }}
                                    </div>
                                </div>

                                <div class="col-md-4">
                                    <div class="text-muted small">
                                        Dokter Pembaca
                                    </div>

                                    <div class="fw-semibold">
                                        {{ showValue(row.DokterPembaca) }}
                                    </div>
                                </div>
                            </div>

                            <div
                                class="border rounded p-3 pre-wrap"
                                v-html="row.Hasil || '-'"
                            ></div>

                            <a
                                v-if="row.photo_url && row.photo_url.photo_url"
                                :href="row.photo_url.photo_url"
                                target="_blank"
                                class="btn btn-sm btn-outline-primary mt-2"
                            >
                                <i class="fa fa-eye me-1"></i>
                                Lihat Citra
                            </a>
                        </div>

                        <EmptyState
                            v-if="!detailViewer.rows.length"
                            text="Detail radiologi tidak tersedia."
                        />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const EmptyState = {
    props: {
        text: {
            type: String,
            required: true,
        },
    },

    template: `
        <div class="text-muted py-3">
            <i class="fa fa-inbox me-1"></i>
            {{ text }}
        </div>
    `,
};

export default {
    name: "UpdateICDRanap",

    components: {
        "v-select": vSelect,
        EmptyState,
    },

    data() {
        return {
            Regno: "",
            pasien: {},
            resumeAvailable: false,
            cppt: [],
            lab: [],
            radiologi: [],
            obat: [],
            icdRows: [],
            icd10Options: [],
            icd9Options: [],
            loadingPage: false,
            errorPage: "",
            saving: false,
            pdfViewer: false,
            detailViewer: null,
            rowCounter: 0,
            loaded: {
                resume: false,
                cppt: false,
                lab: false,
                radiologi: false,
                obat: false,
                icd: false,
            },
            loading: {
                resume: false,
                cppt: false,
                lab: false,
                radiologi: false,
                obat: false,
                icd: false,
            },
        };
    },

    computed: {
        resumePdfUrl() {
            return `/api/icd-ranap/resume-pdf?Regno=${encodeURIComponent(
                this.Regno
            )}`;
        },

        patientInfo() {
            return [
                {
                    label: "Nama Pasien",
                    value: this.pasien.Firstname,
                },
                {
                    label: "Tanggal Lahir",
                    value: this.pasien.TanggalLahir,
                },
                {
                    label: "Usia",
                    value:
                        this.pasien.Usia !== null &&
                        this.pasien.Usia !== undefined
                            ? `${this.pasien.Usia} tahun`
                            : null,
                },
                {
                    label: "Jenis Kelamin",
                    value: this.gender(this.pasien.Sex),
                },
                {
                    label: "Poli Asal",
                    value: this.pasien.NMPoli,
                },
                {
                    label: "Ruang Rawat",
                    value: this.pasien.NmBangsal,
                },
                {
                    label: "DPJP",
                    value: this.pasien.NmDoc,
                },
                {
                    label: "Cara Bayar",
                    value: this.pasien.NMCbayar,
                },
                {
                    label: "No. Registrasi",
                    value: this.pasien.Regno,
                },
                {
                    label: "No. Rekam Medis",
                    value: this.pasien.Medrec,
                },
                {
                    label: "No. SEP",
                    value: this.pasien.NoSep,
                },
                {
                    label: "Waktu Masuk",
                    value: `${this.pasien.TanggalRegistrasi || ""} ${
                        this.pasien.JamRegistrasi || ""
                    }`.trim(),
                },
                {
                    label: "Waktu Pulang",
                    value: `${this.pasien.TanggalPulangFormatted || ""} ${
                        this.pasien.JamPulangFormatted || ""
                    }`.trim(),
                },
                {
                    label: "Diagnosa Awal",
                    value: this.pasien.DiagnosaAwal
                        ? `${this.pasien.KdICD || ""} - ${
                              this.pasien.DiagnosaAwal
                          }`
                        : this.pasien.KdICD,
                    className: "patient-item-wide",
                },
            ];
        },
    },

    created() {
        this.Regno = String(this.$route.params.Regno || "").trim();
    },

    mounted() {
        this.loadPage();
    },

    methods: {
        async loadPage() {
            if (!this.Regno) {
                this.errorPage = "Nomor registrasi tidak ditemukan.";
                return;
            }

            this.loadingPage = true;
            this.errorPage = "";

            try {
                await this.getPatient();
                await this.getIcd();
            } catch (error) {
                this.errorPage =
                    error.response?.data?.message ||
                    error.message ||
                    "Data pasien gagal dimuat.";
            } finally {
                this.loadingPage = false;
            }
        },

        async getPatient() {
            const { data } = await this.axios.get(
                "/api/icd-ranap/detail-pasien",
                {
                    params: {
                        Regno: this.Regno,
                    },
                }
            );

            if (data.status !== "success") {
                throw new Error(data.message || "Data pasien tidak ditemukan.");
            }

            this.pasien = data.data || {};
        },

        async loadHistory(type) {
            if (this.loaded[type] || this.loading[type]) {
                return;
            }

            if (type === "resume") {
                await this.getResume();
            }

            if (type === "cppt") {
                await this.getCppt();
            }

            if (type === "lab") {
                await this.getLab();
            }

            if (type === "radiologi") {
                await this.getRadiology();
            }

            if (type === "obat") {
                await this.getMedicine();
            }
        },

        async runSection(type, callback) {
            this.$set(this.loading, type, true);

            try {
                await callback();
                this.$set(this.loaded, type, true);
            } catch (error) {
                console.error(
                    `Gagal memuat ${type}:`,
                    error.response?.data || error
                );
            } finally {
                this.$set(this.loading, type, false);
            }
        },

        getResume() {
            return this.runSection("resume", async () => {
                const { data } = await this.axios.get(
                    "/api/icd-ranap/resume-final",
                    {
                        params: {
                            Regno: this.Regno,
                        },
                    }
                );

                this.resumeAvailable =
                    data.status === "success" && Boolean(data.data);
            });
        },

        getCppt() {
            return this.runSection("cppt", async () => {
                const { data } = await this.axios.get("/api/icd-ranap/cppt", {
                    params: {
                        Regno: this.Regno,
                        per_page: 100,
                    },
                });

                this.cppt = Array.isArray(data.data?.data)
                    ? data.data.data
                    : [];
            });
        },

        getLab() {
            return this.runSection("lab", async () => {
                const { data } = await this.axios.get("/api/icd-ranap/lab", {
                    params: {
                        Regno: this.Regno,
                        per_page: 100,
                    },
                });

                const payload = data.data || {};

                this.lab = this.rows(payload.lab || payload.data || payload);
            });
        },

        getRadiology() {
            return this.runSection("radiologi", async () => {
                const { data } = await this.axios.get(
                    "/api/icd-ranap/radiologi",
                    {
                        params: {
                            Regno: this.Regno,
                            per_page: 100,
                        },
                    }
                );

                const payload = data.data || {};

                this.radiologi = this.rows(
                    payload.radiologi || payload.data || payload
                );
            });
        },

        getMedicine() {
            return this.runSection("obat", async () => {
                const { data } = await this.axios.get("/api/icd-ranap/obat", {
                    params: {
                        Regno: this.Regno,
                    },
                });

                this.obat = Array.isArray(data.data) ? data.data : [];
            });
        },

        async getIcd() {
            this.$set(this.loading, "icd", true);

            try {
                const { data } = await this.axios.get(
                    "/api/icd-ranap/diagnosa",
                    {
                        params: {
                            Regno: this.Regno,
                        },
                    }
                );

                if (data.status !== "success") {
                    throw new Error(data.message || "Data ICD gagal dimuat.");
                }

                const utama = Array.isArray(data.data?.utama)
                    ? data.data.utama
                    : [];

                const sekunder = Array.isArray(data.data?.sekunder)
                    ? data.data.sekunder
                    : [];

                this.icdRows = [...utama, ...sekunder].map((item) =>
                    this.createIcdRow(item)
                );

                if (!this.icdRows.length) {
                    this.addIcd();
                }

                this.$set(this.loaded, "icd", true);
            } finally {
                this.$set(this.loading, "icd", false);
            }
        },

        createIcdRow(data = {}) {
            this.rowCounter++;

            const icd10 = String(
                data.KdICD || data.KDICD || data.KDICDX || ""
            ).trim();

            const icd9 = String(
                data.KdTDK || data.KdDTD || data.KDICD9 || ""
            ).trim();

            const diagnosis = String(
                data.Diagnosa || data.DIAGNOSA || ""
            ).trim();

            const procedure = String(
                data.Tindakan || data.DiagnosaTindakan || ""
            ).trim();

            return {
                key: `${Date.now()}-${this.rowCounter}`,
                icd10,
                icd9,
                diagnosis: icd10
                    ? {
                          id: icd10,
                          name: diagnosis ? `${icd10} - ${diagnosis}` : icd10,
                      }
                    : null,
                procedure: icd9
                    ? {
                          id: icd9,
                          name: procedure ? `${icd9} - ${procedure}` : icd9,
                      }
                    : null,
                date: data.Tanggal || moment().format("YYYY-MM-DD HH:mm:ss"),
            };
        },

        addIcd() {
            this.icdRows.push(this.createIcdRow());
        },

        removeIcd(index) {
            this.icdRows.splice(index, 1);

            if (!this.icdRows.length) {
                this.addIcd();
            }
        },

        setIcd10(option, index) {
            if (!this.icdRows[index]) {
                return;
            }

            this.icdRows[index].diagnosis = option || null;

            this.icdRows[index].icd10 = option
                ? String(option.id || option.KDICD || option.KdICD || "").trim()
                : "";
        },

        setIcd9(option, index) {
            if (!this.icdRows[index]) {
                return;
            }

            this.icdRows[index].procedure = option || null;

            this.icdRows[index].icd9 = option
                ? String(option.id || option.KDICD || option.KdICD || "").trim()
                : "";
        },

        searchIcd10(search, loading) {
            return this.searchIcd(search, loading, "icd10");
        },

        searchIcd9(search, loading) {
            return this.searchIcd(search, loading, "icd9");
        },

        async searchIcd(search, loading, type) {
            const query = String(search || "").trim();

            if (typeof loading === "function") {
                loading(true);
            }

            if (!query) {
                if (type === "icd10") {
                    this.icd10Options = [];
                } else {
                    this.icd9Options = [];
                }

                if (typeof loading === "function") {
                    loading(false);
                }

                return;
            }

            try {
                const url =
                    type === "icd10"
                        ? "/api/master-icdx/get-icdx"
                        : "/api/master-icd9/get-icd9";

                const { data } = await this.axios.get(url, {
                    params: {
                        q: query,
                    },
                });

                const source =
                    type === "icd10"
                        ? data.data_icdx || data.data || []
                        : data.data_icd9 || data.data || [];

                const options = this.normalizeIcd(source);

                if (type === "icd10") {
                    this.icd10Options = options;
                } else {
                    this.icd9Options = options;
                }
            } catch (error) {
                if (type === "icd10") {
                    this.icd10Options = [];
                } else {
                    this.icd9Options = [];
                }
            } finally {
                if (typeof loading === "function") {
                    loading(false);
                }
            }
        },

        normalizeIcd(data) {
            const rows = Array.isArray(data) ? data : data?.data || [];

            return rows
                .map((row) => {
                    const id = String(
                        row.id || row.KDICD || row.KdICD || row.kode || ""
                    ).trim();

                    const description = String(
                        row.diagnosa ||
                            row.Diagnosa ||
                            row.DIAGNOSA ||
                            row.description ||
                            ""
                    ).trim();

                    return {
                        ...row,
                        id,
                        name:
                            row.name ||
                            (description ? `${id} - ${description}` : id),
                    };
                })
                .filter((row) => row.id);
        },

        async saveIcd() {
            const filled = this.icdRows.filter(
                (row) =>
                    String(row.icd10 || "").trim() ||
                    String(row.icd9 || "").trim()
            );

            if (!filled.length) {
                this.showError("Pilih minimal satu diagnosis atau prosedur.");
                return;
            }

            if (!String(this.icdRows[0]?.icd10 || "").trim()) {
                this.showError("Diagnosis utama ICD-10 wajib diisi.");
                return;
            }

            this.saving = true;

            try {
                const { data } = await this.axios.post(
                    "/api/icd-ranap/save-icd",
                    {
                        Regno: this.Regno,
                        data_icd: filled.map((row) => ({
                            Tanggal: row.date,
                            KDICDX: row.icd10,
                            KDICD9: row.icd9,
                        })),
                    }
                );

                if (data.status !== "success") {
                    throw new Error(data.message || "Data ICD gagal disimpan.");
                }

                await this.$swal.fire({
                    title: "Berhasil",
                    text: data.message || "Data ICD berhasil disimpan.",
                    icon: "success",
                    confirmButtonText: "OK",
                });

                await this.getIcd();
            } catch (error) {
                this.showError(
                    error.response?.data?.message ||
                        error.message ||
                        "Data ICD gagal disimpan."
                );
            } finally {
                this.saving = false;
            }
        },

        openResumePdf() {
            this.pdfViewer = true;
        },

        closeResumePdf() {
            this.pdfViewer = false;
        },

        async openLab(row) {
            this.detailViewer = {
                type: "lab",
                title: row.date || "Detail Laboratorium",
                loading: true,
                head: {},
                groups: {},
                normals: {},
            };

            try {
                const { data } = await this.axios.get(
                    "/api/icd-ranap/lab-detail",
                    {
                        params: {
                            notrans: row.Notran || row.NoTran || row.Nolab,
                        },
                    }
                );

                const payload = data.data || {};

                this.detailViewer.head = payload.head || {};

                this.detailViewer.groups =
                    payload.detail?.arr_detail_pemeriksaan || {};

                this.detailViewer.normals = payload.nilai_normal || {};

                this.detailViewer.loading = false;
            } catch (error) {
                this.closeDetailViewer();

                this.showError("Detail laboratorium gagal dimuat.");
            }
        },

        async openRadiologi(row) {
            this.detailViewer = {
                type: "radiologi",
                title: row.date || row.NmTarif || "Detail Radiologi",
                loading: true,
                rows: [],
            };

            try {
                const { data } = await this.axios.get(
                    "/api/icd-ranap/radiologi-detail",
                    {
                        params: {
                            Regno: this.Regno,
                            notrans: row.NoTran || row.Notran,
                        },
                    }
                );

                this.detailViewer.rows = Array.isArray(data.data)
                    ? data.data
                    : [];

                this.detailViewer.loading = false;
            } catch (error) {
                this.closeDetailViewer();

                this.showError("Detail radiologi gagal dimuat.");
            }
        },

        closeDetailViewer() {
            this.detailViewer = null;
        },

        isCpptPerawat(row) {
            return (
                String(row?.DiisiOleh || "")
                    .trim()
                    .toLowerCase() === "perawat"
            );
        },

        instruction(row) {
            return [
                row.Penunjang,
                row.IntuksiTendakaKesehatan,
                row.instruksi_dokter_spesialis,
                row.Konsul,
            ]
                .map((value) => String(value || "").trim())
                .filter(Boolean)
                .join("\n");
        },

        rows(data) {
            if (Array.isArray(data)) {
                return data;
            }

            if (data && Array.isArray(data.data)) {
                return data.data;
            }

            return [];
        },

        normalValue(row) {
            if (row.NilaiNormal) {
                return row.NilaiNormal;
            }

            const normal = this.detailViewer?.normals?.[row.KDDetail];

            if (!normal) {
                return "-";
            }

            return [normal.nilai_rentang_normal, normal.satuan_nilai]
                .filter(Boolean)
                .join(" ");
        },

        isCriticalLab(row) {
            return Number(row.IsNilaiKritisLab) === 1;
        },

        formatDateTime(value) {
            if (!value) {
                return "-";
            }

            const date = moment(String(value));

            return date.isValid() ? date.format("DD-MM-YYYY HH:mm:ss") : value;
        },

        formatMedicineDate(row) {
            let date = "";
            let time = "";

            if (row.BLDate) {
                const parsedDate = moment(String(row.BLDate));

                date = parsedDate.isValid()
                    ? parsedDate.format("DD-MM-YYYY")
                    : row.BLDate;
            }

            if (row.Jam) {
                const parsedTime = moment(String(row.Jam));

                time = parsedTime.isValid()
                    ? parsedTime.format("HH:mm")
                    : row.Jam;
            }

            return [date, time].filter(Boolean).join(" ") || "-";
        },

        gender(value) {
            if (["L", "M"].includes(value)) {
                return "Laki-laki";
            }

            if (["P", "F"].includes(value)) {
                return "Perempuan";
            }

            return value;
        },

        showValue(value) {
            return value === null || value === undefined || value === ""
                ? "-"
                : value;
        },

        showError(message) {
            this.$swal.fire({
                title: "Error",
                text: message,
                icon: "error",
                confirmButtonText: "OK",
            });
        },
    },
};
</script>

<style scoped>
.patient-card-body {
    padding: 18px 22px 16px;
}

.patient-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-auto-rows: minmax(54px, auto);
    column-gap: 38px;
    row-gap: 8px;
    align-items: start;
}

.patient-item {
    display: grid;
    grid-template-rows: 18px minmax(25px, auto);
    min-width: 0;
    align-content: start;
}

.patient-item-wide {
    grid-column: span 3;
}

.patient-label {
    height: 18px;
    margin: 0;
    color: #6c757d;
    font-size: 0.76rem;
    line-height: 18px;
    text-align: left;
    white-space: nowrap;
}

.patient-value {
    min-width: 0;
    min-height: 25px;
    margin: 0;
    color: #212529;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.35;
    text-align: left;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.patient-status {
    margin-top: 8px;
}

.workspace-row {
    min-height: 560px;
    margin-right: 0;
    margin-left: 0;
}

.icd-card {
    height: calc(100vh - 340px);
    min-height: 560px;
    overflow: visible;
}

.history-card {
    display: flex;
    height: calc(100vh - 340px);
    min-height: 560px;
    flex-direction: column;
    overflow: hidden;
}

.history-scroll {
    min-height: 0;
    flex: 1 1 auto;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
}

.icd-row {
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #dee2e6;
    border-radius: 5px;
    background: #fff;
}

.loading-section {
    padding: 25px 0;
    color: #6c757d;
    text-align: center;
}

.history-accordion .accordion-item {
    border-right: 0;
    border-left: 0;
    border-radius: 0;
}

.history-accordion .accordion-item:first-child {
    border-top: 0;
}

.history-accordion .accordion-button {
    padding: 13px 15px;
    font-size: 0.9rem;
    font-weight: 500;
}

.history-accordion .accordion-button:not(.collapsed) {
    color: #176840;
    background: #e9f7ef;
}

.history-link {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 5px;
    color: #0877c9;
    border: 0;
    border-bottom: 1px solid #eeeeee;
    background: transparent;
    text-align: left;
}

.history-link:hover {
    color: #005a9c;
    background: #f7f9fa;
}

.cppt-table {
    min-width: 850px;
    font-size: 0.78rem;
}

.cppt-table th {
    text-align: center;
    vertical-align: middle;
}

.cppt-table td {
    vertical-align: top;
}

.cppt-soap-cell {
    padding: 0 !important;
    vertical-align: top;
}

.cppt-soap-content {
    min-height: 100%;
    padding: 10px 12px;
    white-space: pre-wrap;
    word-break: break-word;
    transition: padding-left 0.15s ease;
}

.cppt-soap-dokter {
    padding-left: 12px;
}

.cppt-soap-perawat {
    position: relative;
    padding-left: 30%;
}

.cppt-soap-perawat::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 26%;
    width: 3px;
    background: #0d6efd;
    content: "";
}

.cppt-soap-line {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 6px;
    margin-bottom: 8px;
    align-items: start;
}

.cppt-soap-line:last-child {
    margin-bottom: 0;
}

.cppt-soap-line strong {
    text-align: left;
}

.cppt-soap-line span {
    min-width: 0;
    white-space: pre-wrap;
    word-break: break-word;
}

.cppt-row-perawat td {
    background: #fbfdff;
}

.pre-wrap {
    white-space: pre-wrap;
    word-break: break-word;
}

.viewer-overlay {
    position: fixed;
    inset: 0;
    z-index: 1080;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 25px;
    background: rgba(0, 0, 0, 0.68);
}

.viewer-window {
    display: flex;
    width: min(1200px, 100%);
    height: calc(100vh - 50px);
    flex-direction: column;
    overflow: hidden;
    border-radius: 6px;
    background: #ffffff;
}

.viewer-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    color: #ffffff;
    background: #198754;
}

.viewer-content {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
}

.pdf-frame {
    width: 100%;
    height: 100%;
    border: 0;
}

:deep(.vs__dropdown-menu) {
    z-index: 1070;
}

@media (max-width: 1199.98px) {
    .patient-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 26px;
    }

    .patient-item-wide {
        grid-column: span 2;
    }

    .icd-card,
    .history-card {
        height: auto;
        min-height: auto;
    }

    .history-card {
        overflow: visible;
    }

    .history-scroll {
        overflow: visible;
    }
}

@media (max-width: 991.98px) {
    .patient-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        row-gap: 8px;
    }

    .patient-item-wide {
        grid-column: span 2;
    }
}

@media (max-width: 767.98px) {
    .cppt-soap-perawat {
        padding-left: 20%;
    }

    .cppt-soap-perawat::before {
        left: 16%;
    }

    .patient-card-body {
        padding: 15px;
    }

    .patient-grid {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .patient-item-wide {
        grid-column: span 1;
    }

    .viewer-overlay {
        padding: 0;
    }

    .viewer-window {
        width: 100%;
        height: 100vh;
        border-radius: 0;
    }
}
</style>
